/**
 * Utility to parse HTML sections from rendered email templates
 * and map them to element keys from mock-templates.ts
 */

/**
 * Mapping from HTML section IDs (from API response) to element keys (from mock-templates.ts)
 * The HTML uses IDs like "HEADER_V4", "GREETING_V2", etc.
 * These need to be mapped to element keys like "Header", "Welcome", "Personalized Greeting", etc.
 * 
 * Based on actual HTML structure from API response:
 * - HEADER_V4 -> Header
 * - GREETING_V2 -> Personalized Greeting
 * - MONTHLY_SUMMARY_V2 -> Bill Summary
 * - ITEMIZATION_V2 -> Itemization
 * - MONTHLY_USAGE_HISTORY_CHART_V2 -> Bill History
 * - ENERGY_SAVINGS_TIPS_V2 -> Recos (or could be a separate element)
 * - SPONSORED_RECO_V2 -> Recos
 * - TOU_INTERACTION-CTA_BUTTON -> TOU Rate Promotion or Multi-CTA
 * - FEEDBACK_V4 -> User Feedback
 * - FOOTER -> Footer
 */
/**
 * Maps section IDs to user-friendly display names
 * This mapping is used to convert technical section IDs (e.g., "HEADER_V4") 
 * to readable display names (e.g., "Header")
 */
export const SECTION_ID_TO_ELEMENT_KEY_MAP: Record<string, string> = {
  // ---------------------------
  // Header variations
  // ---------------------------
  'HEADER': 'Header',
  'HEADER_V3': 'Header',
  'HEADER_V4': 'Header',

  // ---------------------------
  // Email Preview
  // ---------------------------
  'TEASER': 'Email Preview',

  // ---------------------------
  // New Service variations
  // ---------------------------
  'NEW_SERVICE': 'New Service',
  'NEW_SERVICE_V2': 'New Service',

  // ---------------------------
  // Greeting variations
  // ---------------------------
  'GREETING': 'Personalized Greeting',
  'GREETING_V2': 'Personalized Greeting',
  'PICTURE_GREETING': 'Personalized Picture Greeting',

  // ---------------------------
  // Feedback variations
  // ---------------------------
  'FEEDBACK': 'User Feedback',
  'FEEDBACK_V3': 'User Feedback',
  'FEEDBACK_V4': 'User Feedback',

  // ---------------------------
  // Footer variations
  // ---------------------------
  'FOOTER': 'Footer',
  'FOOTER_V3': 'Footer',
  'FOOTER_V4': 'Footer',
  'NEW_FOOTER': 'Footer',

  // ---------------------------
  // Solar / Energy flow
  // ---------------------------
  'SOLAR_ENERGY_FLOW': 'Solar Energy Flow',
  'SOLAR_ENERGY_FLOW_V2': 'Solar Energy Flow',

  // ---------------------------
  // Surveys
  // ---------------------------
  'SURVEY': 'Survey',

  
  // ---------------------------
  // Chatbot CTA
  // ---------------------------
  'CHATBOT_CTA': 'Chatbot CTA',


  // ---------------------------
  // Recommendations / Tips
  // ---------------------------
  'ENERGY_SAVINGS_TIPS': 'Energy Savings Tips',
  'ENERGY_SAVINGS_TIPS_V2': 'Energy Savings Tips',
  'SEASONAL_RECO': 'Seasonal Reco',
  'SEASONAL_BAR_CHART': 'Seasonal Bar Chart',
  'SPONSORED_RECO': 'Sponsored Reco',
  'SPONSORED_RECO_V2': 'Sponsored Reco',

  // ---------------------------
  // Itemization variations
  // ---------------------------
  'ITEMIZATION': 'Itemization',
  'ITEMIZATION_V2': 'Itemization',
  'ITEMIZATION_EXPLAINER': 'Itemization Explainer',
  'ITEMIZATION_TOP_APPLIANCES': 'Itemization Top Appliances',

  // ---------------------------
  // Monthly Summary variations
  // ---------------------------
  'MONTHLY_SUMMARY': 'Bill Summary',
  'MONTHLY_SUMMARY_V2': 'Bill Summary',

  // ---------------------------
  // Usage History Chart variations
  // ---------------------------
  'MONTHLY_USAGE_HISTORY_CHART': 'Bill History',
  'MONTHLY_USAGE_HISTORY_CHART_V2': 'Bill History',

  // ---------------------------
  // App / SMS / Preferences
  // ---------------------------
  'DOWNLOAD_APP': 'Download App',
  'SMS_SUBSCRIPTION': 'SMS Subscription',
  'ALERTS_PREFERENCES': 'Alerts Preferences',
  'EMAIL_PREFERENCES': 'Email Preferences',

  // ---------------------------
  // Tracking
  // ---------------------------
  'TRACKING': 'Tracking',

  // ---------------------------
  // Business / Budget
  // ---------------------------
  'BUSINESS_PROFILE': 'Business Profile',
  'SET_BUDGET': 'Set Budget',
  'BUDGET_ALERT': 'Budget Alert',
  'UPDATE_BUDGET_AMOUNT': 'Update Budget Amount',
  'BB_CUMULATIVE_BALANCE': 'Budget Billing Balance',

  // ---------------------------
  // Environment Impact
  // ---------------------------
  'ENVIRONMENT_IMPACT': 'Environment Impact',

  // ---------------------------
  // Similar Home Comparison (SHC)
  // ---------------------------
  'SHC_BARGRAPH_COMPARISON': 'Similar Homes Comparison Bargraph',
  'SHC_APPLIANCE_BARGRAPH_COMPARISON': 'Similar Homes Comparison Appliance Bargraph',
  'APPLIANCE_SHC_GRAPH_AND_WIDGET': 'Appliance SHC Graph & Widget',
  'SHC_COMPARISON_WIDGET': 'Similar Homes Comparison',
  'SHC_COMPARISON_WIDGET_V2': 'Similar Homes Comparison',
  'SHC_ENERGY_BREAKDOWN': 'Similar Homes Breakdown',
  'SHC_AND_WEATHER_COMPARE_WIDGET': 'Similar Homes & Weather Comparison',
  'SHC_SUMMARY': 'Similar Homes Summary',
  'SHC_GRAPH_AND_WIDGET_NBI_QR_CODE': 'SHC Graph, NBI & QR Code',
  'SHC_GRAPH_AND_WIDGET_NBI_QR_CODE_WITH_EV': 'SHC Graph, NBI, QR Code & EV',

  // ---------------------------
  // Small Business Comparison (SBC)
  // ---------------------------
  'SBC_COMPARISON_WIDGET': 'Similar Business Comparison',
  'SBC_COMPARISON_WIDGET_V2': 'Similar Business Comparison',
  'SBC_BARGRAPH_COMPARISON': 'Similar Business Comparison Bargraph',

  // ---------------------------
  // Bill Projection
  // ---------------------------
  'BILL_PROJECTION': 'Bill Projection',
  'BILL_PROJECTION_V2': 'Bill Projection',

  // ---------------------------
  // User Welcome / Preview
  // ---------------------------
  'USER_WELCOME': 'User Welcome',

  // ---------------------------  
  // CTA variations
  // ---------------------------
  'CTA': 'CTA',
  'SINGLE_BUTTON': 'Single Button',
  'CTA_WITH_DES_SECTION': 'CTA with Description',
  'CTA_WITH_DES_SECTION_V2': 'CTA with Description',
  'DYNAMIC_CTA': 'Dynamic CTA',

  // ---------------------------
  // Utility Advertisement / Featured Content
  // ---------------------------
  'UTILITY_ADVERTISEMENT': 'Utility Advertisement',
  'FEATURED_CONTENT': 'Featured Content',

  // ---------------------------
  // NBI / Insights family (all share same template section)
  // ---------------------------
  'NBI': 'NBI / Insights',
  'HUA': 'Home Usage Analysis',
  'RATE_COMPARISON': 'Rate Comparison',
  'EE': 'Electricity Efficiency',
  'EV': 'EV Efficiency',

  // ---------------------------
  // Daily / Weekly / Interval analysis
  // ---------------------------
  'DAILY_ANALYSIS': 'Daily Usage Analysis',
  'WEEKLY_COMPARISON': 'Weekly Comparison',
  'DAY_INTERVAL_COMPARISON': 'Day Interval Comparison',

  // ---------------------------
  // Strike charts
  // ---------------------------
  'STRIKE_BAR_GRAPH': 'Strike Bar Graph',
  'STRIKE_DONUT_CHART': 'Strike Donut Chart',

  // ---------------------------
  // EV Sponsored / Welcome / Journey / Monthly tracker
  // ---------------------------
  'EV_SPONSORED_CONTENT': 'EV Sponsored Content',
  'EV_WELCOME': 'EV Welcome',
  'EV_WELCOME_HOOK': 'EV Welcome Hook',
  'EV_WELCOME_JOURNEY_INFO': 'EV Welcome Journey Info',

  'EV_MONTHLY_TRACKER_HEADER': 'EV Monthly Tracker Header',
  'EV_MONTHLY_TRACKER_REWARD': 'EV Monthly Tracker Reward',
  'EV_MONTHLY_TRACKER_COST': 'EV Monthly Tracker Cost',
  'EV_MONTHLY_TRACKER_PEAK_USAGE': 'EV Monthly Tracker Peak Usage',
  'EV_MONTHLY_TRACKER_TOU_PROMO': 'EV Monthly Tracker TOU Promotion',
  'EV_MONTHLY_TRACKER_TOU_CONSUMPTION_SUMMARY': 'EV Monthly Tracker TOU Consumption Summary',

  'EV_TOU_RATE_PLAN_SHORT_DETAIL': 'EV TOU Rate Plan Short Detail',
  'EV_TOU_SIGNUP_CTA': 'EV TOU Signup CTA',
  'EV_STRIKE_INFO': 'EV Strike Information',

  // ---------------------------
  // EV Connectivity failure alert
  // ---------------------------
  'EV_CONNECTIVITY_FAILURE_ALERT_HEADER': 'EV Connectivity Failure Alert Header',
  'EV_CONNECTIVITY_FAILURE_CTA_BUTTON': 'EV Connectivity Failure Alert CTA',
  'EV_CONNECTIVITY_FAILURE_REASON': 'EV Connectivity Failure Alert Reason',

  // ---------------------------
  // EV Promotion emails
  // ---------------------------
  'EV_PROMO_BENEFITS': 'EV Promotion Benefits',
  'EV_PROMO_WELCOME_HOOK': 'EV Promotion Welcome Hook',
  'EV_PROMO_CTA_BUTTON': 'EV Promotion CTA',
  'EV_PROMO_SIMPLE_CTA_BUTTON': 'EV Promotion Simple CTA',
  'EV_ENROLMENT_JOURNEY': 'EV Enrolment Journey',
  'EV_ENROLMENT_WELCOME': 'EV Enrolment Welcome',
  'EV_ENROLMENT_WELCOME_HOOK': 'EV Enrolment Welcome Hook',
  'EV_ENROLMENT_CTA_BUTTON': 'EV Enrolment CTA',
  'EV_PLUG_WELCOME_HOOK': 'EV Plug Welcome Hook',
  'EV_PLUG_CTA_BUTTON': 'EV Plug CTA',

  // ---------------------------
  // TOU onboarding family
  // ---------------------------
  'TOU_ONBOARDING_WELCOME': 'TOU Onboarding Welcome',
  'TOU_RATE_STRUCTURE': 'TOU Rate Structure',
  'TOU_BILL_SAVING_RECO': 'TOU Bill Saving Recommendations',
  'TOU_MOTIVATION': 'TOU Motivation',
  'TOU_USAGE': 'TOU Usage',
  'TOU_RATE_PROMOTION': 'TOU Rate Promotion',

  // ---------------------------
  // Managed charging / Peak alerts
  // ---------------------------
  'MANAGED_CHARGING_PEAK_ALERT': 'Managed Charging Peak Alert',
  'BLS_PEAK_ALERT': 'Peak Alert',

  // ---------------------------
  // OTP / Opt-out / Static
  // ---------------------------
  'OTP_TEXT_SECTION': 'OTP Text',
  'OPT_OUT_CONFIRMATION_SECTION': 'Opt Out Confirmation',
  'STATIC_ELEMENT_SECTION': 'Static Element',

  // ---------------------------
  // Duel fuel (DF) SHC family
  // ---------------------------
  'DF_SHC_BARGRAPH_COMPARISON': 'Dual Fuel Similar Homes Comparison',
  'DF_SHC_SUMMARY': 'Dual Fuel Similar Homes Summary',
  'DF_CTA': 'Dual Fuel CTA',

  // ---------------------------
  // HER (Home Energy Report) sections
  // ---------------------------
  'SHC_GRAPH_AND_WIDGET': 'SHC Graph and Widget',
  'ITEMIZATION_SHC': 'Itemization SHC',
  'PROGRAM_NBI': 'Program NBI',
  'EE_NBI': 'EE NBI',
  'QR_CODE': 'QR Code',
  'ENERGY_FLOW': 'Energy Flow',
  'BILL_CHANGE': 'Bill Change',
  'BAR_CHART': 'Bar Chart',
  'NBI_ACTIONS': 'NBI Actions',

  // ---------------------------
  // Disclaimer
  // ---------------------------
  'DISCLAIMER': 'Disclaimer',
};

/**
 * Extracts section IDs from HTML string
 * Looks for <tr id="SECTION_ID"> patterns
 */
export function extractSectionIds(html: string): string[] {
  if (!html) return [];
  
  const sectionIds: string[] = [];
  // Match <tr id="SECTION_ID"> or <tr id='SECTION_ID'>
  const regex = /<tr\s+id=["']([^"']+)["']/gi;
  let match;
  
  while ((match = regex.exec(html)) !== null) {
    const sectionId = match[1];
    if (sectionId && !sectionIds.includes(sectionId)) {
      sectionIds.push(sectionId);
    }
  }
  
  return sectionIds;
}

/**
 * Maps HTML section IDs to element keys
 */
export function mapSectionIdsToElementKeys(sectionIds: string[]): string[] {
  const elementKeys: string[] = [];
  
  for (const sectionId of sectionIds) {
    // Try exact match first
    let elementKey = SECTION_ID_TO_ELEMENT_KEY_MAP[sectionId];
    
    // If no exact match, try to find by removing version suffix (e.g., _V2, _V3, _V4)
    if (!elementKey) {
      const baseId = sectionId.replace(/_[Vv]\d+$/, '');
      elementKey = SECTION_ID_TO_ELEMENT_KEY_MAP[baseId];
    }
    
    // If still no match, try to find by removing all version suffixes
    if (!elementKey) {
      const cleanId = sectionId.replace(/_[Vv]\d+.*$/, '');
      elementKey = SECTION_ID_TO_ELEMENT_KEY_MAP[cleanId];
    }
    
    // If found, add to list (avoid duplicates)
    if (elementKey && !elementKeys.includes(elementKey)) {
      elementKeys.push(elementKey);
    }
  }
  
  return elementKeys;
}

/**
 * Main function to parse HTML and get element keys
 */
export function parseHtmlSections(html: string): {
  sectionIds: string[];
  elementKeys: string[];
  unmappedIds: string[];
} {
  const sectionIds = extractSectionIds(html);
  const elementKeys = mapSectionIdsToElementKeys(sectionIds);
  const unmappedIds = sectionIds.filter(id => {
    const baseId = id.replace(/_[Vv]\d+$/, '');
    const cleanId = id.replace(/_[Vv]\d+.*$/, '');
    return !SECTION_ID_TO_ELEMENT_KEY_MAP[id] && 
           !SECTION_ID_TO_ELEMENT_KEY_MAP[baseId] && 
           !SECTION_ID_TO_ELEMENT_KEY_MAP[cleanId];
  });
  
  return {
    sectionIds,
    elementKeys,
    unmappedIds
  };
}

/**
 * Filters HTML to show only a specific section
 * @param html - Full HTML string
 * @param sectionId - Section ID to filter (e.g., 'HEADER_V4', 'GREETING_V2')
 * @returns Filtered HTML containing only the specified section, or full HTML if section not found
 */
export function filterHtmlBySection(html: string, sectionId: string | null): string {
  if (!html || !sectionId || sectionId === 'Full Template') {
    return html;
  }
  
  // Use DOMParser - the correct way to parse HTML
  // DOMParser is available in all modern browsers and handles nested structures correctly
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const sectionElement = doc.querySelector(`tr[id="${sectionId}"]`);
  
  if (!sectionElement) {
    // Section not found, return full HTML
    console.warn(`[filterHtmlBySection] Section with id "${sectionId}" not found in HTML`);
    return html;
  }
  
  // Extract the section's outer HTML (includes all nested content)
  const sectionContent = sectionElement.outerHTML;
  
  // Extract styles from original document
  const styles = Array.from(doc.querySelectorAll('style'))
    .map(s => s.textContent)
    .filter(Boolean)
    .join('\n');
  
  // Return a minimal HTML structure with just the section
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    ${styles || `
    body { margin: 0; padding: 0; background-color: #F3F8FD; }
    table { border-spacing: 0; font-family: Arial, Poppins, Helvetica Neue, Helvetica, sans-serif; }
    td { padding: 0; }
    img { border: 0; }
    `}
  </style>
</head>
<body>
  <center class="wrapper" style="width:100%;table-layout:fixed;">
    <div class="webkit" style="background-color:#FFFFFF;max-width:600px;margin:0 auto;">
      <table class="outer" align="center" bgcolor="#FFFFFF" style="border-spacing:0;font-family:Arial, Poppins, Helvetica Neue, Helvetica, sans-serif;Margin:0 auto;width:100%;max-width:600px;background:#FFFFFF">
        ${sectionContent}
      </table>
    </div>
  </center>
</body>
</html>`;
}

/**
 * Gets section ID from element key (reverse mapping)
 * Returns a section ID that actually exists in the HTML, preferring higher versions
 * @param elementKey - The element key (e.g., "Itemization", "Personalized Greeting", "Recos") 
 *                     OR a technical section ID (e.g., "HEADER_V4", "GREETING_V2")
 * @param html - Optional HTML string to verify section ID exists. If not provided, returns first match.
 * @returns Section ID that exists in HTML, or null if not found
 */
export function getSectionIdFromElementKey(elementKey: string, html?: string): string | null {
  if (!elementKey || elementKey === 'Full Template') {
    return null;
  }
  
  // First, check if elementKey is already a technical section ID (exists as a key in the mapping)
  // This handles the case where EditorToolbar passes technical section IDs directly
  if (SECTION_ID_TO_ELEMENT_KEY_MAP[elementKey]) {
    // It's a technical section ID, verify it exists in HTML if provided
    if (html) {
      const escapedId = elementKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`<tr[^>]*id=["']${escapedId}["']`, 'i');
      if (regex.test(html)) {
        return elementKey;
      }
      // If exact match not found, try to find any version of this section in HTML
      // Get the mapped display name to find all related section IDs
      const mappedName = SECTION_ID_TO_ELEMENT_KEY_MAP[elementKey];
      const relatedIds: string[] = [];
      for (const [sectionId, mappedKey] of Object.entries(SECTION_ID_TO_ELEMENT_KEY_MAP)) {
        if (mappedKey === mappedName) {
          relatedIds.push(sectionId);
        }
      }
      // Sort by version (higher first) and find the first one that exists in HTML
      relatedIds.sort((a, b) => {
        const versionA = parseInt(a.match(/_[Vv](\d+)/)?.[1] || '0');
        const versionB = parseInt(b.match(/_[Vv](\d+)/)?.[1] || '0');
        return versionB - versionA;
      });
      for (const sectionId of relatedIds) {
        const escapedSectionId = sectionId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const sectionRegex = new RegExp(`<tr[^>]*id=["']${escapedSectionId}["']`, 'i');
        if (sectionRegex.test(html)) {
          return sectionId;
        }
      }
      console.warn(`[getSectionIdFromElementKey] Technical section ID "${elementKey}" and related versions not found in HTML`);
      return null;
    }
    // No HTML provided, return the technical section ID as-is
    return elementKey;
  }
  
  // elementKey is a mapped display name (e.g., "Header", "Personalized Greeting")
  // Find all matching section IDs from the mapping
  const matchingIds: string[] = [];
  for (const [sectionId, mappedKey] of Object.entries(SECTION_ID_TO_ELEMENT_KEY_MAP)) {
    if (mappedKey === elementKey) {
      matchingIds.push(sectionId);
    }
  }
  
  if (matchingIds.length === 0) {
    // No mapping found - elementKey might be an unmapped technical section ID
    // Try to verify it exists in HTML directly
    if (html) {
      const escapedId = elementKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`<tr[^>]*id=["']${escapedId}["']`, 'i');
      if (regex.test(html)) {
        return elementKey;
      }
    }
    return null;
  }
  
  // Sort by version number (prefer higher versions)
  matchingIds.sort((a, b) => {
    const versionA = parseInt(a.match(/_[Vv](\d+)/)?.[1] || '0');
    const versionB = parseInt(b.match(/_[Vv](\d+)/)?.[1] || '0');
    return versionB - versionA; // Higher version first
  });
  
  // If HTML is provided, verify the section ID exists in the HTML
  if (html) {
    for (const sectionId of matchingIds) {
      // Check if section ID exists in HTML (look for id="SECTION_ID" or id='SECTION_ID')
      const escapedId = sectionId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`<tr[^>]*id=["']${escapedId}["']`, 'i');
      if (regex.test(html)) {
        return sectionId;
      }
    }
    // If none found in HTML, return null
    console.warn(`[getSectionIdFromElementKey] No matching section ID found in HTML for element key "${elementKey}". Tried: ${matchingIds.join(', ')}`);
    return null;
  }
  
  // If no HTML provided, return the first (highest version) match
  return matchingIds[0];
}

